import {Box, Button, Typography} from '@mui/material';
import {MutableRefObject, useContext, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {InviteState} from '../../backend/enums/invite-state';
import {ProcessStatus} from '../../backend/enums/process-status';
import {Invite} from '../../backend/interfaces/invite';
import {RelationshipInviteData} from '../../backend/interfaces/relationship-invite-data';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {usePaginatedTable} from '../../hooks/paginated-table';
import {translations} from '../../i18n/translations';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {InvitesRepository} from '../../repositories/invites';
import {PaginatedTable} from '../paginated-table/paginated-table';
import EmailIcon from '@mui/icons-material/Email';
import {useAuth} from '../../contexts/auth/context';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {CaregiverMode, ScreenPaths} from '../../enums';
import GenericHeader from '../generic-header/generic-header';
import './senior-relationship-invites-history.scss';
import {Modal} from '../modal/modal';
import {InviteSenderType} from '../../backend/enums/invite-sender-type';
import {RelationshipDirection} from '../../backend/enums/relationship-direction';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Contact = 'contact',
  State = 'state',
  Status = 'status',
}

/**
 * Listing of the relationship invites history of a senior.
 * On this screen, it's possible to cancel invites and navigate to the send invite screen.
 *
 * This form was created taking into account that it will exist under the senior tabs.
 * Later on, if it's necessary, we can extend this component to be applied to more contexts.
 */
export const SeniorRelationshipInvitesHistory = (props: {
  seniorId: string;
  seniorName: string;
  entityId?: string;
  inviteCaregiverUrl: string;
  url: string;
  caregiverMode: CaregiverMode;
}) => {
  const [cancelDialogOpened, setCancelDialogOpened] = useState<boolean>(false);
  const [submittingCancel, setSubmittingCancel] = useState<boolean>(false);
  const inviteToCancelRef: MutableRefObject<{id: string; name: string} | undefined> = useRef();
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const {t} = useTranslation();
  const {hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Seniors_PersonalData_Caregivers);
  let history = useHistory();

  // Fetch invites
  const handleGetInvites = async (limit: number, skip: number) => {
    // Load invites
    const result: {totalCount: number; data: Array<Invite<RelationshipInviteData>>} =
      await InvitesRepository.instance.getRelationshipInvitesForUser(
        props.seniorId,
        limit,
        skip,
        RelationshipDirection.CaregiverToSenior
      );

    // Process data
    const processedData: PaginatedTableData<Invite<RelationshipInviteData>> = result.data
      // Analyze data
      .filter((obj) => {
        // Filter out invites sent by seniors
        return obj.senderType !== InviteSenderType.Senior;
      })
      // Transform data
      .map((obj) => {
        // Status
        let status: string = '';
        if (obj.status === ProcessStatus.Done) {
          status = t(translations.senior_relationships_invites_history.process_closed);
        } else {
          status = t(translations.process_statuses[obj.status]);
        }

        // Return row values
        return {
          id: obj.id || '',
          item: obj,
          columns: [
            {
              columnId: ColumnIdentifier.Name,
              value: obj.data?.destinationName || '',
            },
            {
              columnId: ColumnIdentifier.State,
              value: t(translations.invite_states[obj.state]),
            },
            {
              columnId: ColumnIdentifier.Contact,
              value: obj.data?.destinationEmail || obj.data?.destinationPhoneNumber || '',
            },
            {
              columnId: ColumnIdentifier.Status,
              value: status,
            },
          ],
        };
      });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {data, isLoading, error, pagesCount, currentPage, handlePageChange, handleUpdateItem} =
    usePaginatedTable<Invite<RelationshipInviteData>>({
      fetchData: handleGetInvites,
    });

  const handleAskCancelIcon = (inviteId: string) => {
    // Get the caregiver name
    const caregiverName: string | undefined = data
      .find((obj) => obj.id === inviteId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set invite for later
    inviteToCancelRef.current = {
      id: inviteId,
      name: caregiverName || '',
    };

    // Open modal
    setCancelDialogOpened(true);
  };

  const handleDeclineCancelInvite = () => {
    // Close modal
    setCancelDialogOpened(false);

    // Clear
    inviteToCancelRef.current = undefined;
  };

  const handleConfirmCancelInvite = async () => {
    setSubmittingCancel(true);
    try {
      // Make the request to the backend
      await InvitesRepository.instance.cancelInvite(inviteToCancelRef.current?.id || '');

      // Updated item
      let updatedItem = data.find((obj) => obj.id === inviteToCancelRef.current?.id)?.item;
      if (updatedItem) {
        updatedItem.state = InviteState.Cancelled;
        updatedItem.status = ProcessStatus.Done;
      }

      // Update item
      handleUpdateItem(
        inviteToCancelRef.current?.id || '',
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: t(translations.invite_states[InviteState.Cancelled]),
          },
          {
            updatedColumnId: ColumnIdentifier.Status,
            updatedColumnValue: t(translations.senior_relationships_invites_history.process_closed),
          },
        ],
        updatedItem
      );

      // Log
      console.debug(`🟢 Invite cancelled.`, {
        inviteId: inviteToCancelRef.current?.id,
        seniorId: props.seniorId,
        seniorName: props.seniorName,
        caregiverName: inviteToCancelRef.current?.name || '',
      });
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(
          translations.senior_relationships_invites_history.cancel_invite_success_message,
          {
            caregiver: inviteToCancelRef.current?.name || '',
            senior: props.seniorName,
          }
        ),
      });
      setSubmittingCancel(false);
      setCancelDialogOpened(false);
      inviteToCancelRef.current = undefined;
    } catch (error) {
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.senior_relationships_invites_history.cancel_invite_error_message, {
          caregiver: inviteToCancelRef.current?.name || '',
          senior: props.seniorName,
        }),
      });
      setSubmittingCancel(false);
      setCancelDialogOpened(false);
      console.debug(`🔴 Could not cancel the invite.`, {
        inviteId: inviteToCancelRef.current?.id,
        seniorId: props.seniorId,
        seniorName: props.seniorName,
        caregiverName: inviteToCancelRef.current?.name || '',
        error: error,
      });

      // Clear
      inviteToCancelRef.current = undefined;
    }
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {props.caregiverMode === CaregiverMode.Professional && props.entityId && (
        <GenericHeader
          titleCrumbs={[
            {
              label: t(translations.screen_paths.professional_caregiver_seniors),
              link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${props.entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}`,
            },
            {
              label: props.seniorName || '',
              link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${props.entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${props.seniorId}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
            },
            {
              label: t(translations.screen_paths.senior_personal_data),
              link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${props.entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${props.seniorId}${ScreenPaths.Senior_PersonalData}`,
            },
            {
              label: t(translations.screen_paths.senior_caregivers),
              link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${props.entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${props.seniorId}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Caregivers}`,
            },
            {
              label: t(translations.senior_relationships_invites_history.title),
              link: props.url,
            },
          ]}
          isLoading={false}
          leftColumn={<></>}
          rightColumn={
            <Button
              variant="inverted"
              color="primary"
              startIcon={<EmailIcon />}
              onClick={() => {
                history.push(props.inviteCaregiverUrl);
              }}
              disabled={!canWrite}
            >
              {t(translations.senior_relationships_invites_history.invite_caregiver_button)}
            </Button>
          }
        />
      )}
      <Box
        className={`relationships-invites-container${props.caregiverMode === CaregiverMode.SystemAdministrator ? '-sys' : ''}`}
      >
        <Box className="relationships-invites-container-centered">
          <PaginatedTable
            emptyTableMessage={t(translations.senior_relationships_invites_history.no_invites)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.senior_relationships_invites_history.name_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.Contact,
                title: t(translations.senior_relationships_invites_history.contact_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.senior_relationships_invites_history.state_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.Status,
                title: t(translations.senior_relationships_invites_history.status_header),
                flexGrow: 1,
                button: {
                  type: 'text-button',
                  showColumnValue: true,
                  textButton: {
                    label: t(translations.senior_relationships_invites_history.cancel_button),
                    color: 'error',
                    handleClick: handleAskCancelIcon,
                    showOnValues: [
                      t(translations.process_statuses[ProcessStatus.InProgress]),
                      t(translations.process_statuses[ProcessStatus.ToDo]),
                      t(translations.process_statuses[ProcessStatus.Undefined]),
                    ],
                    hideColumnValueWhenShown: true,
                  },
                  disabled: !canWrite,
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={false}
          />
        </Box>
      </Box>
      <Modal
        open={cancelDialogOpened}
        onClose={handleDeclineCancelInvite}
        maxWidth="sm"
        fullWidth
        className="relationships-invites-modal"
        preventCloseClick
        title=""
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: handleDeclineCancelInvite,
          },
          {
            label: t(translations.modal.confirm),
            type: 'nondestructive',
            onClick: handleConfirmCancelInvite,
            loading: submittingCancel,
          },
        ]}
      >
        <Typography className="title">
          {t(translations.senior_relationships_invites_history.cancel_invite_dialog_title, {
            senior: props.seniorName,
            caregiver: inviteToCancelRef.current?.name || '',
          })}
        </Typography>
        <Typography className="subtitle">
          {t(translations.senior_relationships_invites_history.cancel_invite_dialog_message, {
            senior: props.seniorName,
            caregiver: inviteToCancelRef.current?.name || '',
          })}
        </Typography>
      </Modal>
    </Box>
  );
};
